import React, { useCallback, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorRouteProp,
} from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";

import { WidgetSizeT } from "../../localTypes/WidgetSizeT";
import { WAWidgetSizeToOverviewSize } from "../../WidgetAdd/utils/WAWidgetSizeToOverviewSize";

import { overviewWidgetSizes } from "../../WidgetAdd/utils/widgetSizes/overviewWidgetSizes";

import { WidgetOverviewSettings } from "../WidgetOverviewSettings";

import { WidgetScreen } from "../../common/WidgetScreen";
import { WASizePreview } from "../../WidgetAdd/WASizePreview/WASizePreview";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { useWidgetEmployeeRoleOverviewPreviewQuery } from "./useWidgetEmployeeRoleOverviewPreviewQuery";
import { WAEmployeeRoleOverviewMutation } from "./__generated__/WAEmployeeRoleOverviewMutation.graphql";
import { WAEmployeeRoleOverviewQuery } from "./__generated__/WAEmployeeRoleOverviewQuery.graphql";

import { WidgetEmployeeRoleOverviewPreview } from "./WidgetEmployeeRoleOverviewPreview";

type Props = {
  navigation: UserLoggedInNavigatorNavigationProp<"WidgetAddEmployeeRoles">;
  route: UserLoggedInNavigatorRouteProp<"WidgetAddEmployeeRoles">;
};

export const WAEmployeeRoleOverview = withWebModal(
  WAEmployeeRoleOverviewC,
  true
);
function WAEmployeeRoleOverviewC(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <InnerWAEmployeeRoleOverview {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

const WA_EMPLOYEE_ROLE_OVERVIEW_QUERY = graphql`
  query WAEmployeeRoleOverviewQuery {
    store {
      ...WidgetOverviewSettings_Store
    }
  }
`;

function InnerWAEmployeeRoleOverview(props: Props & { fetchKey: string }) {
  const [teamIds, _setTeamIds] = useState(props.route.params.defaultTeams);
  const data = useLazyLoadQuery<WAEmployeeRoleOverviewQuery>(
    WA_EMPLOYEE_ROLE_OVERVIEW_QUERY,
    {},
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const [query, setTeamIds] = useWidgetEmployeeRoleOverviewPreviewQuery({
    teamIds,
    storecastId: props.route.params.storecastId,
    setTeamIds: _setTeamIds,
  });

  const [createWidgetEmployeeRoleOverview, loading] =
    useMutation<WAEmployeeRoleOverviewMutation>(graphql`
      mutation WAEmployeeRoleOverviewMutation(
        $storecastId: ID!
        $input: CreateWidgetEmployeeRoleOverviewInput!
      ) {
        dashboard(dashboardId: $storecastId) {
          createWidgetEmployeeRoleOverview(widget: $input) {
            id
          }
        }
      }
    `);

  const onCreate = useCallback(
    (size: WidgetSizeT) => {
      const position = props.route.params.position;
      createWidgetEmployeeRoleOverview({
        variables: {
          storecastId: props.route.params.storecastId,
          input: {
            positionX: position.x + position.c * 2,
            positionY: position.y,
            size: WAWidgetSizeToOverviewSize(size),
            teamIds,
          },
        },
        onCompleted: () => {
          props.navigation.pop(2);
        },
      });
    },
    [
      createWidgetEmployeeRoleOverview,
      props.navigation,
      props.route.params.position,
      props.route.params.storecastId,
      teamIds,
    ]
  );

  const renderPreview = useCallback(
    (size: WidgetSizeT) =>
      query ? <WidgetEmployeeRoleOverviewPreview query={query} /> : null,
    [query]
  );

  return (
    <WidgetScreen
      preview={
        <WASizePreview
          onCreate={onCreate}
          loading={loading}
          unitSize={props.route.params.unitSize}
          maxSize={props.route.params.maxSize}
          widgetSizes={overviewWidgetSizes}
        >
          {renderPreview}
        </WASizePreview>
      }
      settings={
        <WidgetOverviewSettings
          store={data.store}
          teamIds={teamIds}
          setTeamIds={setTeamIds}
        />
      }
    />
  );
}
